import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/use-api";
import { apiRoomProgressGoogle } from "../../utils/api-routes";
import { ROUTE_ADMIN_QUIZ_ROOM, ROUTE_TEST_MANAGEMENT } from "../../route/teacherRoute";

// Sanitize image URLs returned by the API (preserve valid HTTPS secure-api URLs)
const sanitizeImageUrl = (url) => {
  if (!url) return null;
  let raw = url;
  if (typeof raw === "object") {
    raw = raw.url || raw.src || raw.image_url || raw.image || "";
  }
  if (typeof raw !== "string") return null;

  let trimmed = raw.trim();

  // Strip wrapping quotes and backslashes
  trimmed = trimmed
    .replace(/^(%22|%27|["'\\\/])+/gi, "")
    .replace(/(%22|%27|["'\\\/])+$/gi, "")
    .trim();

  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }

  // Data URLs or blobs
  if (trimmed.startsWith("data:image/") || trimmed.startsWith("blob:")) {
    return trimmed;
  }

  // Relative question_assets paths
  if (trimmed.startsWith("question_assets/")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${trimmed}`;
  } else if (trimmed.startsWith("/question_assets/")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
  } else if (trimmed.includes("question_assets") && !trimmed.startsWith("http")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${assetPart}`;
  } else if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
  }

  // Rovian secure-api
  if (trimmed.includes("rovianai.com/secure-api/")) {
    return trimmed.replace(/^http:\/\//i, "https://");
  }

  // Azure VM URL handling:
  // If given on port 8001, upgrade to https /secure-api/ so browser HTTPS policies don't block it
  if (trimmed.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
    if (trimmed.includes(":8001")) {
      return trimmed
        .replace("http://", "https://")
        .replace(":8001/generated_files/", "/secure-api/generated_files/")
        .replace(":8001/question_assets/", "/secure-api/question_assets/")
        .replace(":8001/", "/secure-api/")
        .replace("visal1chatbotvm.southindia.cloudapp.azure.com:8001", "visal1chatbotvm.southindia.cloudapp.azure.com");
    }
    // Return original HTTPS URL directly
    if (trimmed.startsWith("http://")) {
      return trimmed.replace("http://", "https://");
    }
    return trimmed;
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  return null;
};

export const useClassroomQuizSummaryHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { get } = useApi();

  const roomCode = location.state?.roomCode || "";
  const mode = location.state?.mode || "Async";
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "questions" | "responses"
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState(null);

  useEffect(() => {
    if (roomCode) {
      fetchQuizSummary();
    } else {
      setLoading(false);
    }
  }, [roomCode]);

  const fetchQuizSummary = async () => {
    setLoading(true);
    try {
      const res = await get(apiRoomProgressGoogle(roomCode));
      console.log("progress-google API response:", res);
      if (res?.data) {
        setQuizData(res.data);
      } else if (res) {
        setQuizData(res);
      }
    } catch (err) {
      console.error("Error fetching progress-google:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToRoom = () => {
    if (roomCode) {
      const isChapterName = (val) => {
        if (!val || typeof val !== "string") return false;
        const lower = val.trim().toLowerCase();
        return (
          lower.startsWith("chapter:") ||
          lower.startsWith("chapter :") ||
          lower.startsWith("chapter-") ||
          lower.startsWith("chapter ")
        );
      };
      const stateName = location.state?.testName || location.state?.test_name;
      const validTestName = !isChapterName(stateName)
        ? stateName
        : !isChapterName(quizData?.test_name)
          ? quizData?.test_name
          : "";

      navigate(ROUTE_ADMIN_QUIZ_ROOM, {
        state: {
          roomCode,
          mode,
          testName: validTestName,
          test_name: validTestName,
        },
      });
    } else {
      navigate(ROUTE_TEST_MANAGEMENT);
    }
  };

  // Safe Parsers & Computed Metrics
  const summaryMeta = {
    roomCode: quizData?.room_code || quizData?.roomCode || roomCode || "040269",
    mode: quizData?.mode || mode || "Async",
    courseType: quizData?.course_type || quizData?.courseType || quizData?.exam || "NEET",
    status: quizData?.status ? (quizData.status.charAt(0).toUpperCase() + quizData.status.slice(1)) : "Waiting",
    testName: quizData?.test_name || quizData?.chapter || quizData?.lesson || "Classroom Quiz Summary",
  };

  // Raw Questions Analysis
  const rawQuestions =
    quizData?.questions_analysis ||
    quizData?.question_analysis ||
    quizData?.questions ||
    [];

  // Summary object from API
  const summaryObj = quizData?.summary || {};

  const maxPossibleScore =
    summaryObj.max_possible_score ??
    quizData?.max_possible_score ??
    quizData?.total_questions ??
    (rawQuestions.length > 0 ? rawQuestions.length : 500);

  const totalResponses =
    summaryObj.total_responses ??
    quizData?.total_responses ??
    (quizData?.individual_scores?.length || quizData?.submitted?.length || 1);

  const formatScoreVal = (val) => {
    if (val === undefined || val === null) return `0 / ${maxPossibleScore}`;
    if (typeof val === "string" && val.includes("/")) return val;
    const num = Number(val);
    const formattedNum = Number.isInteger(num) ? num : num.toFixed(1);
    return `${formattedNum} / ${maxPossibleScore}`;
  };

  const averageScore = formatScoreVal(summaryObj.average_score ?? quizData?.average_score ?? quizData?.avg_score);
  const medianScore = formatScoreVal(summaryObj.median_score ?? quizData?.median_score ?? quizData?.med_score);
  const minScore = formatScoreVal(summaryObj.min_score ?? quizData?.min_score ?? quizData?.min_score_val);
  const maxScore = formatScoreVal(summaryObj.max_score ?? quizData?.max_score ?? quizData?.max_score_val);

  // Score Distribution Bucketing
  const rawDistribution =
    summaryObj.score_distribution ||
    quizData?.score_distribution ||
    quizData?.distribution ||
    [];

  let bucket_0_20 = 0;
  let bucket_21_40 = 0;
  let bucket_41_60 = 0;
  let bucket_61_80 = 0;
  let bucket_81_100 = 0;

  if (Array.isArray(rawDistribution)) {
    rawDistribution.forEach((item) => {
      if (item.range) {
        if (item.range === "0-20%") bucket_0_20 += item.count || 0;
        else if (item.range === "21-40%") bucket_21_40 += item.count || 0;
        else if (item.range === "41-60%") bucket_41_60 += item.count || 0;
        else if (item.range === "61-80%") bucket_61_80 += item.count || 0;
        else if (item.range === "81-100%") bucket_81_100 += item.count || 0;
      } else if (item.score !== undefined) {
        const pct = maxPossibleScore > 0 ? (Number(item.score) / maxPossibleScore) * 100 : 0;
        const count = item.count || 1;
        if (pct <= 20) bucket_0_20 += count;
        else if (pct <= 40) bucket_21_40 += count;
        else if (pct <= 60) bucket_41_60 += count;
        else if (pct <= 80) bucket_61_80 += count;
        else bucket_81_100 += count;
      }
    });
  } else if (typeof rawDistribution === "object" && rawDistribution !== null) {
    bucket_0_20 = rawDistribution["0-20"] || 0;
    bucket_21_40 = rawDistribution["21-40"] || 0;
    bucket_41_60 = rawDistribution["41-60"] || 0;
    bucket_61_80 = rawDistribution["61-80"] || 0;
    bucket_81_100 = rawDistribution["81-100"] || 0;
  }

  const scoreDistribution = [
    { range: "0-20%", count: bucket_0_20, color: "#cbd5e1" },
    { range: "21-40%", count: bucket_21_40, color: "#cbd5e1" },
    { range: "41-60%", count: bucket_41_60, color: "#f59e0b" },
    { range: "61-80%", count: bucket_61_80, color: "#6366f1" },
    { range: "81-100%", count: bucket_81_100, color: "#4f46e5" },
  ];

  // Dynamic Y-axis: totalStudents = responses + not started
  const notStartedCount =
    summaryObj.not_started_count ??
    quizData?.not_started_count ??
    0;
  const totalStudents = Number(totalResponses) + Number(notStartedCount);

  // Y-axis max: minimum 10, otherwise actual total
  const yMax = Math.max(totalStudents, 10);

  // Step = ceil(yMax / 5) so we always get ~5 intervals (6 ticks)
  const yStep = Math.ceil(yMax / 5);

  // Build descending ticks array: [yMax, yMax-step, ..., 0]
  const yTicks = [];
  for (let v = yMax; v > 0; v -= yStep) {
    yTicks.push(v);
  }
  yTicks.push(0);

  const yAxisConfig = { yMax, yStep, yTicks };

  // Individual Scores Summary
  // API returns individual_responses; also support legacy individual_scores / submitted fields
  const rawIndividual =
    quizData?.individual_responses ||
    quizData?.individual_scores ||
    summaryObj?.individual_scores ||
    quizData?.submitted ||
    [];

  const individualScores = rawIndividual.map((item, idx) => {
    const scoreVal = item.score !== undefined ? item.score : (summaryObj.average_score || 0);
    const maxVal = item.max_possible_score ?? item.max_score ?? maxPossibleScore;
    const correctCount = item.correct_count ?? item.correct ?? 0;
    const totalCount = item.total_questions ?? item.total ?? (rawQuestions.length || 0);
    const pctRaw = item.percentage ?? (maxVal > 0 ? ((scoreVal / maxVal) * 100).toFixed(1) : "0.0");
    const percentage = String(pctRaw).includes("%") ? pctRaw : `${pctRaw}%`;

    return {
      rank: item.rank !== undefined ? `#${item.rank}` : `#${idx + 1}`,
      nickname: item.nickname || item.name || "Student",
      email: item.email || item.mail || "—",
      score: `${scoreVal} / ${maxVal}`,
      correct: correctCount,
      total: totalCount,
      percentage,
    };
  });

  // Not Started List
  const rawNotStarted =
    quizData?.not_started ||
    summaryObj?.not_started ||
    quizData?.pending ||
    [];

  const notStartedStudents = rawNotStarted.map((item) => ({
    nickname: item.nickname || item.name || "Student",
    email: item.email || item.mail || "-",
  }));

  // Questions Analysis parser
  const questionsList = rawQuestions.map((q, idx) => {
    const distributionList = q.distribution || q.options || [];
    
    // Find skipped item in distribution if present
    const skippedItem = distributionList.find(
      (d) => d.choice_id === null || d.choice_text?.toLowerCase() === "skipped" || d.is_skipped
    );
    const regularOptions = distributionList.filter(
      (d) => d !== skippedItem
    );

    const options = regularOptions.map((opt, oIdx) => ({
      text: opt.choice_text || opt.text || (opt.choice_id ? `Option (${opt.choice_id})` : `Option ${oIdx + 1}`),
      count: opt.count ?? 0,
      isCorrect: opt.is_correct ?? (opt.choice_id === q.correct_answer),
      choiceImage: sanitizeImageUrl(opt.choice_image || opt.image_url || null),
    }));

    const correctPercentage = q.correct_percentage !== undefined
      ? Math.round(Number(q.correct_percentage))
      : q.total_responses > 0
        ? Math.round(((q.correct_count || 0) / q.total_responses) * 100)
        : 0;

    const skippedCount =
      q.skipped_count !== undefined && q.skipped_count !== null
        ? Number(q.skipped_count)
        : skippedItem?.count !== undefined && skippedItem?.count !== null
        ? Number(skippedItem.count)
        : 0;

    // Parse question images robustly (supporting array, string, stringified JSON)
    let rawImg =
      q.image_urls ??
      q.imageUrls ??
      q.images ??
      q.image_url ??
      q.imageUrl ??
      q.image ??
      q.question_image ??
      q.diagram ??
      null;

    if (typeof rawImg === "string") {
      const trimmedRaw = rawImg.trim();
      if (trimmedRaw.startsWith("[") && trimmedRaw.endsWith("]")) {
        try {
          rawImg = JSON.parse(trimmedRaw);
        } catch (e) {
          rawImg = [trimmedRaw];
        }
      }
    }

    let parsedImageUrls = [];
    if (Array.isArray(rawImg)) {
      parsedImageUrls = rawImg.map(sanitizeImageUrl).filter(Boolean);
    } else if (rawImg) {
      const sanitized = sanitizeImageUrl(rawImg);
      if (sanitized) parsedImageUrls = [sanitized];
    }

    const rawQuestionText = (q.question_text || q.question || "").trim();
    const isImagePlaceholder = (text) => {
      if (!text) return true;
      const lower = text.toLowerCase().trim().replace(/[-_]/g, " ");
      return (
        lower === "image based question" ||
        lower === "image based" ||
        lower === "image based questions" ||
        lower === "image question" ||
        lower === "image questions" ||
        lower.startsWith("image based question") ||
        lower === `question ${idx + 1}` ||
        lower === `question ${q.question_id || q.id}`
      );
    };

    const hasImages = parsedImageUrls && parsedImageUrls.length > 0;
    const shouldHideText = hasImages && isImagePlaceholder(rawQuestionText);
    const cleanedQuestionText = shouldHideText ? "" : (rawQuestionText || (hasImages ? "" : `Question ${idx + 1}`));

    return {
      id: q.question_id || q.id || idx + 1,
      questionNumber: idx + 1,
      questionText: cleanedQuestionText,
      rawQuestionText: rawQuestionText || `Question ${idx + 1}`,
      correctAnswer: q.correct_text || q.correct_answer || q.answer || "Correct Answer",
      type: q.question_type || q.type || "MCQ",
      totalResponses: q.total_responses ?? totalResponses,
      correctCount: q.correct_count ?? 0,
      incorrectCount: q.incorrect_count ?? 0,
      correctPercentage,
      options,
      skippedCount,
      imageUrls: parsedImageUrls,
    };
  });

  // Individual Responses Matrix
  const rawResponsesMatrix =
    quizData?.individual_responses ||
    quizData?.student_matrix ||
    quizData?.submitted ||
    (individualScores.length > 0 ? individualScores : []);

  let matrixQuestions = [];
  if (quizData?.matrix_questions && Array.isArray(quizData.matrix_questions) && quizData.matrix_questions.length > 0) {
    matrixQuestions = quizData.matrix_questions;
  } else if (questionsList && questionsList.length > 0) {
    matrixQuestions = questionsList.map((q, idx) => ({
      key: `q${idx + 1}`,
      id: q.id,
      title: `Q${idx + 1}`,
      questionText: q.questionText || q.rawQuestionText || `Question ${idx + 1}`,
    }));
  } else if (rawResponsesMatrix.length > 0 && Array.isArray(rawResponsesMatrix[0]?.answers) && rawResponsesMatrix[0].answers.length > 0) {
    matrixQuestions = rawResponsesMatrix[0].answers.map((a, idx) => ({
      key: `q${idx + 1}`,
      id: a.question_id || idx + 1,
      title: `Q${idx + 1}`,
      questionText: a.question_text || `Question ${idx + 1}`,
    }));
  } else {
    matrixQuestions = [];
  }

  const matrixStudents = rawResponsesMatrix.map((s, idx) => {
    const rawAnswers = s.answers || s.responses || [];
    const normalizedResponses = rawAnswers.map((ans, aIdx) => {
      const isCorrect =
        ans.is_correct !== undefined
          ? Boolean(ans.is_correct)
          : ans.isCorrect !== undefined
          ? Boolean(ans.isCorrect)
          : false;

      const isSkipped =
        ans.chosen_answer === null ||
        ans.chosen_answer === undefined ||
        ans.chosen_text?.toLowerCase() === "skipped" ||
        ans.is_skipped;

      const answerText =
        ans.chosen_text ||
        ans.chosen_answer ||
        ans.answerText ||
        (isSkipped ? "Skipped" : "—");

      return {
        questionId: ans.question_id || ans.questionId || (aIdx + 1),
        questionText: ans.question_text || ans.questionText || "",
        chosenAnswer: ans.chosen_answer,
        chosenText: ans.chosen_text,
        correctAnswer: ans.correct_answer,
        correctText: ans.correct_text,
        answerText,
        isCorrect,
        isSkipped,
      };
    });

    const maxVal = s.max_possible_score ?? s.max_score ?? maxPossibleScore;
    const scoreVal = s.score !== undefined ? s.score : (summaryObj.average_score || 0);
    const pctRaw =
      s.percentage !== undefined
        ? s.percentage
        : maxVal > 0
        ? ((scoreVal / maxVal) * 100).toFixed(1)
        : "0.0";
    const percentage = String(pctRaw).includes("%") ? pctRaw : `${pctRaw}%`;

    return {
      nickname: s.nickname || s.name || `Student ${idx + 1}`,
      email: s.email || s.mail || "—",
      score: `${scoreVal} / ${maxVal}`,
      percentage,
      responses: normalizedResponses,
    };
  });

  return {
    roomCode,
    activeTab,
    setActiveTab,
    loading,
    quizData,
    summaryMeta,
    kpis: {
      totalResponses,
      averageScore,
      medianScore,
      minScore,
      maxScore,
    },
    scoreDistribution,
    yAxisConfig,
    individualScores,
    notStartedStudents,
    questionsList,
    matrixStudents,
    matrixQuestions,
    handleBackToRoom,
    fetchQuizSummary,
  };
};
